/**
 * Rendering helper functions for canvas elements.
 * Extracted from CanvasElement.tsx for reusability and maintainability.
 */
import { LayoutElement } from '../types';
import { metersToEffectiveWorldUnits } from './measurement';

/**
 * Calculate optimal font size for element label based on height.
 * @param height - Element height in canvas units
 * @returns Font size between 4 and 6
 */
export const getLabelFontSize = (height: number) =>
  Math.max(4, Math.min(6, Math.round(height * 0.2)));

/**
 * Truncate a label to fit within element width with ellipsis if needed.
 * @param name - Original element name
 * @param width - Available width in canvas units
 * @param fontSize - Font size in canvas units
 * @returns Truncated name with ellipsis or original name if it fits
 */
export const getDisplayName = (name: string, width: number, fontSize: number) => {
  const maxChars = Math.max(3, Math.floor((width - 4) / (fontSize * 0.55)));
  if (name.length <= maxChars) return name;
  return `${name.slice(0, Math.max(2, maxChars - 1))}…`;
};

/**
 * Get track stroke width from element properties or standard default.
 * @param el - Layout element
 * @param floorPlanScaleFactor - Optional floor plan scale factor
 * @param dxfUnitsPerMeter - Optional DXF units per meter
 * @returns Stroke width in canvas units (minimum: 0.1, standard: 4)
 */
export const getTrackStrokeWidth = (
  el: LayoutElement,
  floorPlanScaleFactor?: number,
  dxfUnitsPerMeter?: number
) => {
  const thicknessMeters = Number(el.customProperties?.thicknessMeters);
  if (Number.isFinite(thicknessMeters) && thicknessMeters > 0) {
    return Math.max(
      0.1,
      metersToEffectiveWorldUnits(thicknessMeters, floorPlanScaleFactor, dxfUnitsPerMeter)
    );
  }
  // Standard thickness for all OHT rails on canvas
  return 4;
};

/**
 * Get curve radius from width and height dimensions.
 * @param width - Curve width in canvas units
 * @param height - Curve height in canvas units
 * @returns Radius (minimum: 0.001)
 */
export const getCurveRadius = (width: number, height: number) => {
  return Math.max(0.001, Math.min(width, height));
};

/**
 * Get stroke width for a curve element.
 * @param element - Layout element
 * @param floorPlanScaleFactor - Optional floor plan scale factor
 * @param dxfUnitsPerMeter - Optional DXF units per meter
 * @returns Stroke width in canvas units
 */
export const getCurveStrokeWidth = (
  element: LayoutElement,
  floorPlanScaleFactor?: number,
  dxfUnitsPerMeter?: number
) => {
  return getTrackStrokeWidth(element, floorPlanScaleFactor, dxfUnitsPerMeter);
};

/**
 * Generate curve points for rendering an arc (Bezier approximation).
 * @param radius - Radius of the arc
 * @param sweepAngle - Total sweep angle in degrees
 * @param sweepSign - Direction: 1 for CCW, -1 for CW (default: 1)
 * @returns Flat array of x, y coordinates for Konva Line
 */
export const getCurvePoints = (radius: number, sweepAngle: number, sweepSign: 1 | -1 = 1) => {
  const segments = Math.max(8, Math.ceil(Math.abs(sweepAngle) / 5));
  const points: number[] = [];
  const signedSweep = sweepSign * sweepAngle;

  for (let i = 0; i <= segments; i++) {
    const angle = (signedSweep * i) / segments;
    const rad = (angle * Math.PI) / 180;
    points.push(radius * Math.cos(rad), radius * Math.sin(rad));
  }

  return points;
};

/**
 * Snap a rotation angle to the nearest 45-degree increment.
 * @param angle - Rotation angle in degrees (0-360)
 * @returns Snapped angle in degrees (0-360)
 */
export const snapRotationTo45 = (angle: number) => {
  const snapped = Math.round(angle / 45) * 45;
  return ((snapped % 360) + 360) % 360;
};

/**
 * Get the Figma-style rotate cursor SVG as a data URL.
 * @returns CSS cursor string with encoded SVG
 */
export const getRotateCursor = () => {
  const svg = `<svg width="30" height="30" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M5.92719 9.91431H14.0064C18.4684 9.91431 22.0856 13.5315 22.0856 17.9935V26.0727" stroke="black" stroke-width="2" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M9.91433 5.92725L5.92719 10.0193L9.91433 14.1114" stroke="black" stroke-width="2" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M18.0985 22.0857L22.0857 26.0728L26.0728 22.0857" stroke="black" stroke-width="2" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 12 12, auto`;
};
